import { Button, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useMSSP from "../../../../api/hooks/useMSSP";
import { platformUsersKeys } from "../../../../api/queryKeys";
import { Toast } from "../../../../components/Toast";
import { useFileNaming } from "../../../../hooks/useFileNaming";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons } from "../../../stack/environment/icons/iconExport";

function ExportMitrePdfButton({ containerRef }) {
  const [isExporting, setIsExporting] = useState(false);
  const { trackEvent, getPlatformUser } = useAPI();
  const colors = useColors();
  const { isMSSP, currentTenantId } = useMSSP();
  const { generatePdfFileName } = useFileNaming();
  // Get platform user data to access customer_id for non-MSSP users
  const { data: platformUser } = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  // Determine the customer ID based on user type
  const getCustomerId = () => {
    if (isMSSP) {
      return currentTenantId;
    }
    return platformUser?.customer_id;
  };

  async function handleExport() {
    trackEvent(
      "User click on 'Export' button to download the MITRE ATT&CK view to PDF"
    );
    if (!containerRef.current) {
      Toast.error("No content available to export to PDF");
      return;
    }

    setIsExporting(true);
    try {
      const container = containerRef.current;
      const customerId = getCustomerId();
      const currentDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      // Create a clone of the container to avoid modifying the original
      const clone = container.cloneNode(true);
      if (!(clone instanceof HTMLElement)) {
        throw new Error("Failed to clone the container");
      }

      // Create a wrapper div that will contain the header and the cloned content
      const wrapper = document.createElement("div");
      wrapper.style.position = "absolute";
      wrapper.style.left = "-9999px";
      wrapper.style.top = "-9999px";
      wrapper.style.overflow = "visible";
      wrapper.style.backgroundColor = "#ffffff";
      wrapper.style.padding = "20px";
      wrapper.style.fontFamily = "Arial, sans-serif";

      // Create and add header with date and customer ID
      const header = document.createElement("div");
      header.style.marginBottom = "20px";
      header.style.borderBottom = "2px solid #e0e0e0";
      header.style.paddingBottom = "10px";

      const title = document.createElement("h1");
      title.textContent = "MITRE ATT&CK Framework View";
      title.style.margin = "0 0 10px 0";
      title.style.fontSize = "24px";
      title.style.fontWeight = "bold";
      title.style.color = "#333";

      const dateInfo = document.createElement("p");
      dateInfo.textContent = `Generated on: ${currentDate}`;
      dateInfo.style.margin = "5px 0";
      dateInfo.style.fontSize = "14px";
      dateInfo.style.color = "#666";

      const customerInfo = document.createElement("p");
      customerInfo.textContent = `Customer: ${customerId || "N/A"}`;
      customerInfo.style.margin = "5px 0";
      customerInfo.style.fontSize = "14px";
      customerInfo.style.color = "#666";

      header.appendChild(title);
      header.appendChild(dateInfo);
      header.appendChild(customerInfo);

      // Prepare the clone for rendering
      clone.style.position = "relative";
      clone.style.overflow = "visible";
      clone.style.maxHeight = "none";
      clone.style.height = "auto";
      clone.style.width = container.scrollWidth + "px"; // Use scroll width to capture all content
      clone.style.whiteSpace = "nowrap";

      // Add header and content to wrapper
      wrapper.appendChild(header);
      wrapper.appendChild(clone);

      // Process each technique card for export
      const paperElements = clone.querySelectorAll(".MuiPaper-root");
      paperElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          // Don't change the original structure, just modify styles
          element.style.height = "auto";
          element.style.minHeight = "68px";

          // Find the caption (technique name) and update its styles
          const captionElement = element.querySelector(
            ".MuiTypography-caption"
          );
          if (captionElement instanceof HTMLElement) {
            captionElement.style.whiteSpace = "normal";
            captionElement.style.wordBreak = "break-word";
            captionElement.style.overflow = "visible";
            captionElement.style.textOverflow = "clip";
            captionElement.style.maxHeight = "none";
            // Keep the flex layout and allow it to grow
            captionElement.style.flexGrow = "1";
          }

          // Ensure the percentage label stays in position
          const percentageElement = element.querySelector(".MuiBox-root");
          if (percentageElement instanceof HTMLElement) {
            percentageElement.style.flexShrink = "0";
            percentageElement.style.alignSelf = "flex-start";
          }
        }
      });

      // Remove any max-width/max-height constraints
      const allElements = clone.querySelectorAll("*");
      allElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.overflow = "visible";
          el.style.maxHeight = "none";
          el.style.boxShadow = "none";
        }
      });

      // Append wrapper to body temporarily
      document.body.appendChild(wrapper);

      // Ensure all content is visible and measurable
      const actualWidth = wrapper.scrollWidth;
      const actualHeight = wrapper.scrollHeight;

      wrapper.style.width = actualWidth + "px";
      wrapper.style.height = actualHeight + "px";

      // Create canvas from the prepared wrapper with reduced quality for smaller file size
      const canvas = await html2canvas(wrapper, {
        scale: 1.5, // Reduced from 2 to 1.5 for smaller file size
        useCORS: true,
        allowTaint: true,
        backgroundColor: colors.common_white,
        width: actualWidth,
        height: actualHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: actualWidth,
        windowHeight: actualHeight,
        logging: false,
        imageTimeout: 0, // No timeout
      });

      // Remove the wrapper from document
      document.body.removeChild(wrapper);

      // Get image data with reduced quality for smaller file size
      const imgData = canvas.toDataURL("image/jpeg", 0.7); // Use JPEG instead of PNG with 70% quality

      // Create PDF in landscape orientation
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        compress: true, // Enable PDF compression
      });

      // Calculate PDF dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate image dimensions to maintain aspect ratio
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add the first page with compression
      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      ); // Use FAST compression

      // Add additional pages if needed
      let heightLeft = imgHeight - pdfHeight;
      let position = -pdfHeight; // Starting position for the second page

      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          "FAST"
        );

        heightLeft -= pdfHeight;
        position -= pdfHeight;
      }

      // Save the PDF with compression
      pdf.save(generatePdfFileName("mitre_attack_view"));
    } catch (error) {
      Toast.error("Failed to export MITRE ATT&CK view, Please try again later");
      trackEvent("Failed to export MITRE ATT&CK view", {
        error: error.message,
      });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Button
      startIcon={
        <SvgToIcon
          IconLogo={icons.ExportPDFIcon}
          style={{ width: "18px", color: "transparent" }}
        />
      }
      onClick={handleExport}
      disabled={isExporting}
      sx={{
        height: "35px",
        border: "1px solid " + colors.grey_32percent,
        textTransform: "unset",
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: colors.text_secondary,
        }}
      >
        {isExporting ? "Exporting..." : "Export to PDF"}
      </Typography>
    </Button>
  );
}

export default ExportMitrePdfButton;
